import { useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

export default function SignalerProbleme() {
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const validate = () => {
    if (!nom.trim()) return "Le nom est requis.";
    if (!description.trim() && !image) return "Veuillez fournir une description ou une capture d'écran.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("nom", nom.trim());
      formData.append("description", description.trim());
      if (image) formData.append("image", image);
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/signaler-probleme`, formData);
      setSuccess(true);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...styles.page, justifyContent: "center", alignItems: "center", padding: "20px" }}>
        <div style={{ ...styles.card, maxWidth: "520px", width: "100%", padding: "50px 30px", textAlign: "center", position: "relative", overflow: "hidden", border: "1px solid rgba(0,0,0,0.05)" }}>
          {/* Decorative glow */}
          <div style={{ position: "absolute", top: "-100px", left: "50%", transform: "translateX(-50%)", width: "300px", height: "300px", background: "radial-gradient(circle, rgba(34,197,94,0.1) 0%, rgba(255,255,255,0) 70%)", zIndex: 0 }} />
          
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "25px" }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" className="checkmark-svg">
                <circle cx="12" cy="12" r="10" className="checkmark-circle" />
                <polyline points="9 12 11 14 15 10" className="checkmark-check" />
              </svg>
            </div>
            
            <h2 style={{ margin: "0 0 16px", fontSize: "1.8rem", fontWeight: 800, color: "#1e293b", letterSpacing: "-0.5px" }}>
              Rapport envoyé avec succès !
            </h2>
            
            <p style={{ margin: "0 auto 35px", fontSize: "1rem", color: "#64748b", lineHeight: 1.7, maxWidth: "400px" }}>
              Merci d'avoir pris le temps de nous alerter. <br />
              Notre équipe technique a été notifiée et va examiner votre signalement dans les plus brefs délais.
            </p>
            
            <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
              <button 
                onClick={() => { setSuccess(false); setNom(""); setDescription(""); removeImage(); }} 
                style={{ ...styles.backBtn, background: "#f8fafc", color: "#475569", border: "1.5px solid #e2e8f0", margin: 0, cursor: "pointer" }}
              >
                Nouveau signalement
              </button>
              <Link to="/" style={{ ...styles.backBtn, background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", color: "#ffffff", border: "none", margin: 0 }}>
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>

        <style>{`
          .checkmark-circle {
            stroke-dasharray: 166;
            stroke-dashoffset: 166;
            stroke-width: 1.5;
            stroke-miterlimit: 10;
            stroke: #22c55e;
            fill: rgba(34,197,94,0.05);
            animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
          }
          .checkmark-check {
            transform-origin: 50% 50%;
            stroke-dasharray: 48;
            stroke-dashoffset: 48;
            animation: stroke 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.6s forwards;
          }
          @keyframes stroke {
            100% { stroke-dashoffset: 0; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header bar */}
      <div style={styles.navbar}>
        <Link to="/" style={styles.navLogo}>WYN</Link>
        <Link to="/" style={styles.navBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Accueil
        </Link>
      </div>

      <div style={styles.wrapper}>
        {/* Left side — decorative */}
        <div style={styles.leftPanel}>
          <div style={styles.leftContent}>
            <div style={styles.bugIllustration}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5">
                <path d="M12 2a4 4 0 0 1 4 4v2H8V6a4 4 0 0 1 4-4z" />
                <path d="M8 8v2a4 4 0 0 0 8 0V8" />
                <line x1="2" y1="10" x2="6" y2="12" />
                <line x1="22" y1="10" x2="18" y2="12" />
                <line x1="2" y1="18" x2="6" y2="16" />
                <line x1="22" y1="18" x2="18" y2="16" />
                <path d="M8 12v6a4 4 0 0 0 8 0v-6" />
              </svg>
            </div>
            <h1 style={styles.leftTitle}>Signaler un problème</h1>
            <p style={styles.leftText}>
              Vous avez trouvé un bug ou rencontré une anomalie dans l'application ?
              Aidez-nous à améliorer WYN en nous soumettant votre signalement.
            </p>
            <div style={styles.featureList}>
              {[
                "Réponse rapide de notre équipe",
                "Toutes les données sont sécurisées",
                "Votre retour améliore l'app",
              ].map((f) => (
                <div key={f} style={styles.featureItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFC300" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right side — form */}
        <div style={styles.rightPanel}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardIcon}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFC300" strokeWidth="2.5">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </div>
              <div>
                <h2 style={styles.cardTitle}>Nouveau signalement</h2>
                <p style={styles.cardSubtitle}>Envoyé à wyncontact.ma@gmail.com</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              {/* Nom field */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Nom complet <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Votre nom..."
                  style={styles.input}
                />
              </div>

              {/* Description field */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="17" y1="10" x2="3" y2="10" />
                    <line x1="21" y1="6" x2="3" y2="6" />
                    <line x1="21" y1="14" x2="3" y2="14" />
                    <line x1="17" y1="18" x2="3" y2="18" />
                  </svg>
                  Description du problème{" "}
                  <span style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 400 }}>
                    (requis si pas d'image)
                  </span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez le problème que vous avez rencontré..."
                  rows={4}
                  style={{ ...styles.input, resize: "vertical", lineHeight: 1.6 } as React.CSSProperties}
                />
              </div>

              {/* Image upload */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  Capture d'écran{" "}
                  <span style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 400 }}>
                    (requis si pas de description)
                  </span>
                </label>

                {!preview ? (
                  <div
                    style={styles.uploadZone}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const f = e.dataTransfer.files?.[0];
                      if (f && f.type.startsWith("image/")) {
                        setImage(f);
                        setPreview(URL.createObjectURL(f));
                      }
                    }}
                  >
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p style={{ margin: "10px 0 4px", color: "#64748b", fontWeight: 600, fontSize: "0.9rem" }}>
                      Glissez une image ici
                    </p>
                    <p style={{ margin: 0, color: "#94a3b8", fontSize: "0.8rem" }}>
                      ou <span style={{ color: "#FFC300", fontWeight: 600 }}>cliquez pour sélectionner</span>
                    </p>
                  </div>
                ) : (
                  <div style={styles.previewWrapper}>
                    <img src={preview} alt="preview" style={styles.previewImg} />
                    <button type="button" onClick={removeImage} style={styles.removeBtn}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} style={{ display: "none" }} />
              </div>

              {error && (
                <div style={styles.errorBanner}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              <button type="submit" style={styles.submitBtn} disabled={sending}>
                {sending ? (
                  <>
                    <span style={styles.spinner} /> Envoi en cours...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                    Envoyer le signalement
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
    display: "flex",
    flexDirection: "column",
  },
  navbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 32px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  navLogo: {
    color: "#FFC300",
    fontWeight: 800,
    fontSize: "1.4rem",
    textDecoration: "none",
    letterSpacing: "2px",
  },
  navBack: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#94a3b8",
    textDecoration: "none",
    fontSize: "0.88rem",
    fontWeight: 500,
    transition: "color 0.2s",
  },
  wrapper: {
    display: "flex",
    flex: 1,
    maxWidth: "1100px",
    margin: "0 auto",
    width: "100%",
    padding: "40px 24px",
    gap: "40px",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  leftPanel: {
    flex: "1 1 320px",
    paddingTop: "20px",
    minWidth: "280px",
  },
  leftContent: {
    color: "#fff",
  },
  bugIllustration: {
    width: "80px",
    height: "80px",
    background: "rgba(255,195,0,0.12)",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "24px",
    border: "1px solid rgba(255,195,0,0.2)",
  },
  leftTitle: {
    fontSize: "1.9rem",
    fontWeight: 800,
    margin: "0 0 14px",
    color: "#fff",
    lineHeight: 1.2,
  },
  leftText: {
    color: "#94a3b8",
    lineHeight: 1.7,
    marginBottom: "28px",
    fontSize: "0.95rem",
  },
  featureList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#cbd5e1",
    fontSize: "0.9rem",
  },
  rightPanel: {
    flex: "1 1 400px",
    minWidth: "280px",
    width: "100%",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    overflow: "hidden",
  },
  successIcon: {
    display: "flex",
    justifyContent: "center",
    padding: "40px 0 20px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "24px 28px",
    borderBottom: "1px solid #f1f5f9",
    background: "#fafafa",
  },
  cardIcon: {
    width: "48px",
    height: "48px",
    background: "linear-gradient(135deg, #1a1a2e, #16213e)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitle: {
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1e293b",
  },
  cardSubtitle: {
    margin: 0,
    fontSize: "0.8rem",
    color: "#64748b",
  },
  form: {
    padding: "28px",
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.88rem",
    fontWeight: 600,
    color: "#374151",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "0.92rem",
    color: "#1e293b",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box" as const,
  },
  uploadZone: {
    border: "2px dashed #e2e8f0",
    borderRadius: "12px",
    padding: "32px",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s",
    background: "#f8fafc",
  },
  previewWrapper: {
    position: "relative" as const,
    borderRadius: "12px",
    overflow: "hidden",
    border: "1.5px solid #e2e8f0",
  },
  previewImg: {
    width: "100%",
    maxHeight: "220px",
    objectFit: "contain" as const,
    background: "#f1f5f9",
    display: "block",
  },
  removeBtn: {
    position: "absolute" as const,
    top: "10px",
    right: "10px",
    background: "#ef4444",
    border: "none",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    borderRadius: "10px",
    padding: "10px 14px",
    color: "#dc2626",
    fontSize: "0.88rem",
    fontWeight: 500,
  },
  submitBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "14px",
    background: "linear-gradient(135deg, #FFC300, #e6a800)",
    border: "none",
    borderRadius: "12px",
    color: "#1a1a2e",
    fontWeight: 700,
    fontSize: "0.98rem",
    cursor: "pointer",
    transition: "opacity 0.2s, transform 0.1s",
    fontFamily: "inherit",
  },
  spinner: {
    width: "18px",
    height: "18px",
    border: "2.5px solid rgba(26,26,46,0.2)",
    borderTopColor: "#1a1a2e",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.7s linear infinite",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 24px",
    background: "#f1f5f9",
    borderRadius: "10px",
    color: "#1e293b",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.92rem",
    margin: "0 auto 40px",
  },
  title: {
    textAlign: "center" as const,
    fontWeight: 800,
    fontSize: "1.6rem",
    margin: "0 0 12px",
  },
  subtitle: {
    textAlign: "center" as const,
    color: "#64748b",
    lineHeight: 1.6,
    fontSize: "0.95rem",
    margin: "0 auto 24px",
    maxWidth: "360px",
    padding: "0 24px",
  },
};
